import { fetcher } from '../../../lib/api-fetcher';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export type PayoutStatus =
  | 'PENDING_SURETY'
  | 'PENDING_APPROVAL'
  | 'APPROVED'
  | 'INSTRUCTION_CREATED'
  | 'PAID'
  | 'FAILED'
  | 'CANCELLED'
  | 'REVERSED';

export interface PrizePayoutItem {
  id: string;
  schemeId: string;
  cycleId: string;
  auctionId: string;
  winnerSubscriptionId: string;
  grossPrize: number;
  tdsOnPrize: number;
  netPayout: number;
  status: PayoutStatus;
  suretyClearedAt: string | null;
  instructionId: string | null;
  paidAt: string | null;
  confirmedBy: string | null;
  createdAt: string;
}

export interface PrizePayoutDetail extends PrizePayoutItem {
  makerId: string | null;
  checkerId: string | null;
  failureReason: string | null;
  failedAt: string | null;
  retryCount: number;
  reversalReason: string | null;
  reversedAt: string | null;
  cancelledReason: string | null;
}

export interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PrizePayoutListResponse {
  data: PrizePayoutItem[];
  meta: PaginationMeta;
}

export interface DividendCreditItem {
  id: string;
  schemeId: string;
  cycleId: string;
  auctionId: string;
  subscriptionId: string;
  dividendAmount: number;
  appliedToDueId: string | null;
  createdAt: string;
}

export const payoutKeys = {
  all: ['payouts'] as const,
  lists: () => [...payoutKeys.all, 'list'] as const,
  list: (params: object) => [...payoutKeys.lists(), params] as const,
  detail: (id: string) => [...payoutKeys.all, 'detail', id] as const,
  dividends: (subId: string) => ['dividends', 'subscriber', subId] as const,
  schemeDividends: (schemeId: string) => ['dividends', 'scheme', schemeId] as const,
};

export function usePayoutsList(params: {
  page: number;
  limit: number;
  status?: string;
  schemeId?: string;
}) {
  return useQuery<PrizePayoutListResponse>({
    queryKey: payoutKeys.list(params),
    queryFn: async () => {
      const qs = new URLSearchParams({ page: String(params.page), limit: String(params.limit) });
      if (params.status) qs.append('status', params.status);

      const url = params.schemeId
        ? `/staff/v1/dividends/schemes/${params.schemeId}/prize-payouts?${qs}`
        : `/staff/v1/dividends/prize-payouts?${qs}`;

      return fetcher.get<PrizePayoutListResponse>(url);
    },
  });
}

export function usePayoutDetail(id: string | null) {
  return useQuery<{ data: PrizePayoutDetail | null }>({
    queryKey: payoutKeys.detail(id ?? ''),
    queryFn: () => fetcher.get<{ data: PrizePayoutDetail | null }>(`/staff/v1/dividends/prize-payouts/${id}`),
    enabled: !!id,
  });
}

export function useApprovePayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetcher.post<{ success: boolean; moneyInstructionId: string }>(`/staff/v1/dividends/prize-payouts/${id}/approve`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payoutKeys.all });
    },
  });
}

export function useConfirmPayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetcher.post<{ success: boolean }>(`/staff/v1/dividends/prize-payouts/${id}/confirm`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payoutKeys.all });
    },
  });
}

export function useFailPayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      fetcher.post<{ success: boolean }>(`/staff/v1/dividends/prize-payouts/${id}/fail`, { reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payoutKeys.all });
    },
  });
}

export function useRetryPayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetcher.post<{ success: boolean; moneyInstructionId: string }>(`/staff/v1/dividends/prize-payouts/${id}/retry`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payoutKeys.all });
    },
  });
}

export function useReversePayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reversalReason }: { id: string; reversalReason: string }) =>
      fetcher.post<{ success: boolean; approvalRequestId?: string }>(`/staff/v1/dividends/prize-payouts/${id}/reverse`, { reversalReason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payoutKeys.all });
    },
  });
}

export function useSubscriberDividends(subscriptionId: string | null) {
  return useQuery<{ data: DividendCreditItem[] }>({
    queryKey: payoutKeys.dividends(subscriptionId ?? ''),
    queryFn: () => fetcher.get<{ data: DividendCreditItem[] }>(`/staff/v1/dividends/subscribers/${subscriptionId}/dividends`),
    enabled: !!subscriptionId,
  });
}
